import { useQuery } from "@tanstack/react-query";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { QueryState } from "@/components/common/AsyncState";
import { KpiCard } from "@/components/common/KpiCard";
import { fetchFinanceReport } from "@/api/finance";
import { formatMoney, formatPercent, periodLabel } from "@/utils/format";

export function ReportTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["finance-report"],
    queryFn: () => fetchFinanceReport(),
  });

  const months = data?.months ?? [];
  const current = months[months.length - 1];
  const totals = months.reduce(
    (acc, m) => ({ revenue: acc.revenue + m.revenue, salaries: acc.salaries + m.salaries, otherExpenses: acc.otherExpenses + m.otherExpenses, profit: acc.profit + m.profit }),
    { revenue: 0, salaries: 0, otherExpenses: 0, profit: 0 },
  );

  return (
    <QueryState isLoading={isLoading} error={error}>
      {data && (
        <Grid container spacing={2}>
          {current && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Приход" value={formatMoney(current.revenue)} hint={periodLabel(current.period)} />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Зарплаты"
                  value={formatMoney(current.salaries)}
                  hint={current.revenue ? `${Math.round((current.salaries / current.revenue) * 100)}% от прихода` : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard
                  label="Прочие расходы"
                  value={formatMoney(current.otherExpenses)}
                  hint={current.revenue ? `${Math.round((current.otherExpenses / current.revenue) * 100)}% от прихода` : undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <KpiCard label="Прибыль" value={formatMoney(current.profit)} />
              </Grid>
            </>
          )}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} mb={2}>
                Прибыль по месяцам (приход − зарплаты − прочие расходы)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data.months.map((m) => ({ ...m, label: periodLabel(m.period) }))}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" />
                  <YAxis tickFormatter={(v) => formatMoney(v)} width={110} />
                  <Tooltip formatter={(v: number) => formatMoney(v)} />
                  <Legend />
                  <Bar dataKey="revenue" name="Приход" fill="#2E5AAC" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="salaries" name="Зарплаты" fill="#F2994A" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="otherExpenses" name="Прочие расходы" fill="#CBD5E1" radius={[4, 4, 0, 0]} />
                  <Line type="monotone" dataKey="profit" name="Прибыль" stroke="#2E7D32" strokeWidth={3} />
                </ComposedChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined">
              <Typography variant="subtitle1" fontWeight={600} p={2}>
                Отчёт о прибылях и убытках
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Месяц</TableCell>
                    <TableCell align="right">Приход</TableCell>
                    <TableCell align="right">Зарплаты преподавателей</TableCell>
                    <TableCell align="right">Прочие расходы</TableCell>
                    <TableCell align="right">Прибыль</TableCell>
                    <TableCell align="right">Рентабельность</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {months
                    .slice()
                    .reverse()
                    .map((m) => (
                      <TableRow key={m.period}>
                        <TableCell>
                          <b>{periodLabel(m.period)}</b>
                        </TableCell>
                        <TableCell align="right">{formatMoney(m.revenue)}</TableCell>
                        <TableCell align="right">{formatMoney(m.salaries)}</TableCell>
                        <TableCell align="right">{formatMoney(m.otherExpenses)}</TableCell>
                        <TableCell align="right">
                          <b style={{ color: m.profit >= 0 ? "var(--mui-palette-success-main)" : "var(--mui-palette-error-main)" }}>
                            {formatMoney(m.profit)}
                          </b>
                        </TableCell>
                        <TableCell align="right">{m.revenue ? `${Math.round((m.profit / m.revenue) * 100)}%` : "—"}</TableCell>
                      </TableRow>
                    ))}
                  <TableRow sx={{ backgroundColor: "action.hover" }}>
                    <TableCell>
                      <b>Итого за {months.length} мес.</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{formatMoney(totals.revenue)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{formatMoney(totals.salaries)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{formatMoney(totals.otherExpenses)}</b>
                    </TableCell>
                    <TableCell align="right">
                      <b>{formatMoney(totals.profit)}</b>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined">
              <Typography variant="subtitle1" fontWeight={600} p={2}>
                Сводка по филиалам
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Филиал</TableCell>
                    <TableCell>Учеников</TableCell>
                    <TableCell>Групп</TableCell>
                    <TableCell>Приход</TableCell>
                    <TableCell>План</TableCell>
                    <TableCell>% выполнения плана</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.branches.map((b) => (
                    <TableRow key={b.branchId}>
                      <TableCell>{b.branchName}</TableCell>
                      <TableCell>{b.students}</TableCell>
                      <TableCell>{b.groups}</TableCell>
                      <TableCell>{formatMoney(b.revenue)}</TableCell>
                      <TableCell>{formatMoney(b.plan)}</TableCell>
                      <TableCell>{formatPercent(b.planPercent)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        </Grid>
      )}
    </QueryState>
  );
}
